// EL AÑO DE UNA PÓLIZA: el de la fecha en que EMPEZÓ (la fecha efectiva).
//
// Lissette, 2026-09-13: *"me han preguntado mucho si ellos pueden tener como
// especie de orden de pólizas por año; imagino que podemos poner un filtro de
// año dependiendo del año que comenzó la póliza, y a su vez colocarle el año en
// la esquina de la póliza"*.
//
// Vive suelto y no dentro de una pantalla porque lo miran TRES: el filtro de
// Pólizas, la esquina de cada tarjeta y la tabla de Comisiones por Póliza (que
// llevaba su propia copia de esta línea). Si el año se leyera de dos maneras,
// el filtro diría 2025 y la tarjeta 2024 en la misma fila.
//
// SE LEE EL TEXTO, NO SE CONSTRUYE UNA FECHA. Las fechas llegan como
// `YYYY-MM-DD` (o un ISO completo) y los primeros cuatro caracteres son el año,
// sin más. Pasar por `new Date(...)` metería el huso horario en una pregunta
// que no lo necesita: una póliza del 1 de enero de 2025 se leería como 2024 en
// Florida (regla de la casa: la fecha, en local; y aquí ni siquiera hace falta
// fecha).

/** El año de inicio, como texto de cuatro cifras. Sin fecha efectiva: ''. */
export function anioDePoliza(poliza) {
  const crudo = String((poliza && poliza.effectiveDate) || '').trim();
  const anio = crudo.slice(0, 4);
  return /^\d{4}$/.test(anio) ? anio : '';
}

/**
 * Los años que hay EN LA CARTERA, del más nuevo al más viejo y con cuántas
 * pólizas tiene cada uno: `[{ anio: '2025', cuantas: 48 }, …]`.
 *
 * El número va en el desplegable a propósito — un filtro que dice cuántas hay
 * detrás de cada opción evita el clic a ciegas que no devuelve nada.
 */
export function aniosDePolizas(polizas) {
  const cuenta = new Map();
  for (const p of (Array.isArray(polizas) ? polizas : [])) {
    const anio = anioDePoliza(p);
    if (!anio) continue;
    cuenta.set(anio, (cuenta.get(anio) || 0) + 1);
  }
  return [...cuenta.entries()]
    .map(([anio, cuantas]) => ({ anio, cuantas }))
    .sort((a, b) => b.anio.localeCompare(a.anio));
}

/** Cuántas pólizas no tienen fecha efectiva, que son las que ningún año puede
 *  enseñar. Se ofrecen aparte en el filtro: si no, se quedan escondidas y
 *  parecen perdidas (una pantalla que esconde filas tiene que decirlo). */
export function polizasSinAnio(polizas) {
  return (Array.isArray(polizas) ? polizas : []).filter((p) => !anioDePoliza(p)).length;
}

/** ¿Pasa esta póliza el filtro de año? `'all'` deja pasar todo y `'none'` son
 *  justo las que no tienen fecha efectiva. */
export function pasaElFiltroDeAnio(poliza, filtro) {
  if (!filtro || filtro === 'all') return true;
  const anio = anioDePoliza(poliza);
  if (filtro === 'none') return !anio;
  return anio === filtro;
}

/**
 * Las pólizas ORDENADAS por el año en que empezaron.
 *
 * Lissette, 2026-09-13, después de ver el filtro: *"si quiero el orden por año
 * también"*. Vive aquí y no en la pantalla por lo mismo que el resto: si el
 * orden leyera el año de otra manera que el filtro, elegir 2025 y ordenar por
 * año darían listas que no se explican entre sí.
 *
 * `direccion` es `'desc'` (las más nuevas primero, lo normal en una cartera) o
 * `'asc'`. Dentro del mismo año se sigue por la fecha completa, así que el
 * orden es de verdad cronológico y no un montón por año sin ordenar dentro.
 *
 * LAS QUE NO TIENEN FECHA EFECTIVA VAN AL FINAL SIEMPRE, en las dos
 * direcciones. No es un empate que se resuelva solo: en `'asc'` una póliza sin
 * año se colaría delante de la más vieja de verdad —y el primer renglón de la
 * lista es justo lo que la gente lee— así que se quedan juntas al final, donde
 * se ven y no estorban. No se toca el array que llega.
 */
export function ordenarPorAnio(polizas, direccion = 'desc') {
  // `asc` = la más vieja delante; `desc` = la más nueva delante.
  const signo = direccion === 'asc' ? -1 : 1;
  // La fecha completa como texto: `YYYY-MM-DD` se compara en orden con solo
  // mirar los caracteres, y un ISO largo cae en su sitio con los diez
  // primeros. Sin `new Date`, por lo mismo que arriba.
  const fecha = (p) => String((p && p.effectiveDate) || '').trim().slice(0, 10);
  return (Array.isArray(polizas) ? [...polizas] : []).sort((a, b) => {
    const fa = anioDePoliza(a) ? fecha(a) : '';
    const fb = anioDePoliza(b) ? fecha(b) : '';
    if (!fa && !fb) return 0;
    if (!fa) return 1;   // sin año: al final
    if (!fb) return -1;
    return fa < fb ? signo : fa > fb ? -signo : 0;
  });
}
